/**
 * Player card showing name, avatar, and connection status.
 *
 * The `tv` variant fills the box given by its parent and sizes its content in
 * container units, so the whole card scales with the cell computed by the grid.
 */

import { useTranslation } from 'react-i18next'
import type { Player } from '../../types/player'
import { Card } from '@/components/ui/card'
import { PixelIcon } from '../../components/low/PixelIcon'

export type PlayerCardVariant = 'phone' | 'tv'

interface PlayerCardProps {
  player: Player
  isCurrentUser?: boolean
  isPinging?: boolean
  variant?: PlayerCardVariant
}

interface PlayerCardSizes {
  card: string
  unitLabel: string
  status: string
  statusDot: string
  statusText: string
  avatarArea: string
  avatar: string
  avatarIcon: string
  hostBadge: string
  name: string
  readyRow: string
  readyLine: string
  readyText: string
}

const PHONE_SIZES: PlayerCardSizes = {
  card: 'p-6',
  unitLabel: 'top-2 right-4 text-[8px]',
  status: 'top-4 left-4 gap-2',
  statusDot: 'w-1.5 h-1.5',
  statusText: 'text-[8px]',
  avatarArea: 'mt-4 mb-4',
  avatar: 'w-20 h-20',
  avatarIcon: 'text-4xl',
  hostBadge: 'text-[8px] px-2 py-0.5',
  name: 'text-xl',
  readyRow: 'mt-2 gap-2',
  readyLine: 'w-4',
  readyText: 'text-[10px]',
}

const TV_SIZES: PlayerCardSizes = {
  card: 'h-full w-full p-[5cqh] flex flex-col justify-center',
  unitLabel: 'top-[2cqh] right-[4cqw] text-[3cqh]',
  status: 'top-[3cqh] left-[4cqw] gap-[1.5cqw]',
  statusDot: 'w-[2cqh] h-[2cqh]',
  statusText: 'text-[3cqh]',
  avatarArea: 'mt-[4cqh] mb-[4cqh]',
  avatar: 'w-[34cqh] h-[34cqh]',
  avatarIcon: 'text-[17cqh]',
  hostBadge: 'text-[3cqh] px-[2cqw] py-[0.5cqh]',
  name: 'text-[8cqh]',
  readyRow: 'mt-[2cqh] gap-[2cqw]',
  readyLine: 'w-[6cqw]',
  readyText: 'text-[3.5cqh]',
}

export function PlayerCard({
  player,
  isCurrentUser = false,
  isPinging = false,
  variant = 'phone',
}: PlayerCardProps) {
  const { t } = useTranslation()
  const isConnected = player.connection_status === 'connected'
  const isDisconnected = player.connection_status === 'disconnected'
  const isAway = player.connection_status === 'away'
  const sizes = variant === 'tv' ? TV_SIZES : PHONE_SIZES

  const card = (
    <Card
      data-testid={`player-card-${player.name}`}
      className={`
        relative ${sizes.card} overflow-hidden transition-all duration-500
        ${isPinging ? 'scale-105 border-primary shadow-[0_0_30px_color-mix(in_srgb,var(--primary)_40%,transparent)]' : 'border-border shadow-xl'}
        ${isDisconnected || isAway ? 'opacity-40 grayscale' : 'opacity-100'}
        ${isCurrentUser ? 'bg-muted' : ''}
      `}
    >
      {/* Ping Ripple Effect */}
      {isPinging && (
        <div className="absolute inset-0 bg-primary/20 animate-ping rounded-lg pointer-events-none" />
      )}

      {/* HUD Accents */}
      <div className={`absolute ${sizes.unitLabel} font-black text-muted-foreground uppercase tracking-widest`}>
        {t('game.unitLabel')} {player.id.slice(0, 4)}
      </div>

      {/* Connection Status Indicator */}
      <div className={`absolute ${sizes.status} flex items-center`}>
        <div className={`${sizes.statusDot} rounded-full ${
          isConnected ? 'bg-primary shadow-[0_0_8px_var(--primary)]'
          : isAway ? 'bg-yellow-500'
          : 'bg-muted-foreground'
        }`} />
        <span className={`${sizes.statusText} font-bold uppercase tracking-tighter ${
          isConnected ? 'text-primary'
          : isAway ? 'text-yellow-500'
          : 'text-muted-foreground'
        }`}>
          {isConnected ? t('game.linkActive') : isAway ? t('game.away', 'Away') : t('game.offline')}
        </span>
      </div>

      {/* Avatar Area */}
      <div className={`${sizes.avatarArea} flex justify-center relative`}>
        <div className={`
          ${sizes.avatar} rounded-full flex items-center justify-center border-2 transition-colors duration-500
          ${isPinging ? 'border-primary bg-primary/20' : 'border-border bg-background'}
        `}>
          {isPinging ? (
            <PixelIcon className={`${sizes.avatarIcon} animate-bounce`}>⚡</PixelIcon>
          ) : (
            <PixelIcon className={`${sizes.avatarIcon} grayscale-[0.5]`}>🙂</PixelIcon>
          )}
        </div>

        {player.is_host && (
          <div className={`absolute -bottom-1 bg-primary text-primary-foreground ${sizes.hostBadge} font-black rounded uppercase tracking-widest shadow-lg`}>
            {t('game.hostBadge')}
          </div>
        )}
      </div>

      {/* Name & Info */}
      <div className="text-center">
        <p className={`${sizes.name} font-black text-foreground truncate uppercase tracking-wider`}>
          {player.name}
        </p>
        <div className={`${sizes.readyRow} flex items-center justify-center`}>
          <div className={`h-[1px] ${sizes.readyLine} ${player.is_ready ? 'bg-primary' : 'bg-border'}`} />
          <span className={`${sizes.readyText} font-bold uppercase tracking-[0.2em] ${player.is_ready ? 'text-primary' : 'text-muted-foreground'}`}>
            {player.is_ready ? t('game.ready') : t('game.standby')}
          </span>
          <div className={`h-[1px] ${sizes.readyLine} ${player.is_ready ? 'bg-primary' : 'bg-border'}`} />
        </div>
      </div>
    </Card>
  )

  if (variant === 'phone') return card

  // Container units inside the card resolve against this wrapper, i.e. the grid cell.
  return <div className="h-full w-full [container-type:size]">{card}</div>
}
